import React, { useEffect, useState } from 'react'
import { EconomicAnalysis } from '../components/EconomicAnalysis'
import {
  AggregationProfit,
  GenerateAmount,
} from '../components/EconomicAnalysis/index' // [추가] 분리된 컴포넌트들
import LabelOnly from '../components/LabelOnly'
import { useLazyQuery, useQuery } from '@apollo/client'
import {
  findOneBuyTOUGQL,
  findPVDataGQL,
  getHolidaysGQL,
} from '../gql/PurchasePlans.gql'
import ReactECharts from 'echarts-for-react'
import { EChartsOption } from 'echarts'
import { findLoadDataGQL, findOneSellTOUGQL } from '../gql/SellPlans.gql'
import { findAllESSGQL } from '../gql/ESS.gql'
import { findAllUnitDefaultGQL } from './DataUnit'

// [수정] 새롭게 개편된 styled-components 임포트
import {
  ContentWrapper,
  Body,
  GraphBox,
  Header,
  Title,
  InputSection,
  ResultOverview,
  ResultColumn,
  ResultPanel,
  ProfitBox,
  ProfitBody,
  ProfitAmountColumn,
  ProfitDivider,
  ProfitDetailColumn,
  ProfitStrongDivider,
  ProfitTotalRow,
  SummaryGrid,
  DetailSection,
  DetailGrid,
  DetailFullWidth,
  TopControls,
  ControlItem,
  RightAlignedControls,
  NumberInput,
  MiddleItem,
  MiddleItemContent,
  ScrollTableBox,
  Table,
  TableCell,
  TableRow,
  SectionFrame,
  SectionHeader,
  SectionMark,
  SectionTitle,
  SectionDivider,
} from './EconomicESS.styled'

import { divTimeData } from '../util/algorithm/classifyTimeData'
import { seasonMapping } from '../util/SeasonMapping'
import {
  calMonthlyChargeAsUsage,
  sumMonthNumToTotalNum,
} from '../util/algorithm/calCharge'
import { ITimeDataItem } from '../util/algorithm/algoInterface'
import { calUsingPV } from '../util/algorithm/economicAlgo'
import { InputLabel } from './PurchasePlans.styled'
import AutoCompleteOnly from '../components/AutoCompleteOnly'
import { buildPeakDeductionLoadData } from '../util/algorithm/peakDeductionSellPlan'

// =======================================
// [신규] 독립적인 3등분 요약 카드 컴포넌트
// =======================================
interface SummaryCardProps {
  title: string
  subTitle: string
  value: string | number
  color?: string
}

const SummaryCard: React.FC<SummaryCardProps> = ({
  title,
  subTitle,
  value,
  color = '#333',
}) => (
  <div
    style={{
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      padding: '20px 16px',
      background: '#fff',
      borderRadius: '8px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
      border: '1px solid #e6e6e6',
      height: '100%',
      boxSizing: 'border-box',
    }}
  >
    <div
      style={{ display: 'flex', flexDirection: 'column', marginBottom: '12px' }}
    >
      <span
        style={{
          fontSize: '18px',
          fontWeight: '700',
          color: '#333',
          lineHeight: '1.2',
          fontFamily: 'SUIT',
        }}
      >
        {title}
      </span>
      <span
        style={{
          fontSize: '12px',
          color: '#888',
          marginTop: '4px',
          fontFamily: 'SUIT',
          wordBreak: 'keep-all',
        }}
      >
        {subTitle}
      </span>
    </div>
    <div
      style={{
        fontSize: '22px',
        fontWeight: '700',
        color: color,
        fontFamily: 'SUIT',
        wordBreak: 'keep-all',
      }}
    >
      {value}
    </div>
  </div>
)
// =======================================

export interface IEconomicAnalysis {
  index: String
  plan: { _id: string | undefined }
  customer: { _id: string | undefined }
}
interface YearlyData {
  year: number
  revenue: number
  pvCost: number
  gridCost: number
  opex: number
  cumulative: number
}
interface ResultState {
  npv: number
  irr: string
  paybackPeriod: string
  chartDataX: string[]
  chartDataY: number[]
  cumulativeCashFlow: number
  yearlyDetails: YearlyData[]
}

const calculateIRR = (cashFlows: number[], guess = 0.1): string => {
  const maxIter = 1000
  const precision = 0.00001
  let rate = guess

  for (let i = 0; i < maxIter; i++) {
    let npv = 0
    let derivative = 0

    for (let t = 0; t < cashFlows.length; t++) {
      npv += cashFlows[t] / Math.pow(1 + rate, t)
      derivative -= (t * cashFlows[t]) / Math.pow(1 + rate, t + 1)
    }

    const newRate = rate - npv / derivative

    if (Math.abs(newRate - rate) < precision) {
      return (newRate * 100).toFixed(2)
    }
    rate = newRate
  }
  return '계산 불가'
}

export const EconomicESS = () => {
  const { data: unitDefault } = useQuery(findAllUnitDefaultGQL)
  const [loadIdList, setLoadIdList] = useState<IEconomicAnalysis[]>([])
  const [pvIdList, setPvIdList] = useState<IEconomicAnalysis[]>([])

  const [EssData, setEssData] = useState<any>({
    holidayYear: '',
    selectedEssName: '',
  })

  const [findAllESS, { data: essListData }] = useLazyQuery(findAllESSGQL, {
    fetchPolicy: 'network-only',
  })
  const [essNames, setEssNames] = useState<string[]>([])
  const [fullEssList, setFullEssList] = useState<any[]>([])

  const [findHolidaysData] = useLazyQuery(getHolidaysGQL)
  const [holidaysData, setHolidaysData] = useState<any>()

  const [findPVData] = useLazyQuery(findPVDataGQL)
  const [findOnePurchasePlanData] = useLazyQuery(findOneBuyTOUGQL)
  const [PVDataList, setPVDataList] = useState<any[]>()

  const [findLoadData] = useLazyQuery(findLoadDataGQL)
  const [findOneSellTOUData] = useLazyQuery(findOneSellTOUGQL)
  const [LoadDataList, setLoadDataList] = useState<any>()
  const [flag, setFlag] = useState<Boolean>(false)

  const [discountRateInput, setDiscountRateInput] = useState<number>(4.5)
  // [수정] 변동률(음수 가능)로 변수명 및 로직 변경
  const [smpVariationRate, setSmpVariationRate] = useState<number>(0)
  const [kepcoIncreaseRate, setKepcoIncreaseRate] = useState<number>(0)

  const [loadCharge, setLoadCharge] = useState<
    {
      custName: string
      touName: string
      load: number
      charge: number
      gridCharge: number
    }[]
  >([])
  const [ESSLoadData, setESSLoadData] = useState<ITimeDataItem[][]>([])

  const [result, setResult] = useState<ResultState>({
    npv: 0,
    irr: '0',
    paybackPeriod: '0',
    chartDataX: [],
    chartDataY: [],
    cumulativeCashFlow: 0,
    yearlyDetails: [],
  })

  const [selectedESS, setSelectedESS] = useState<any>(null)
  const [essFinancials, setEssFinancials] = useState({
    capex: 0,
    opex: 0,
    period: 0,
    opexInputMode: 'REGISTRATION',
  })
  const [analysisOpexRateInput, setAnalysisOpexRateInput] = useState<number>(0)

  useEffect(() => {
    setAnalysisOpexRateInput(0)
  }, [EssData.selectedEssName])

  useEffect(() => {
    if (EssData.selectedEssName && fullEssList.length > 0) {
      const target = fullEssList.find(
        (e: any) => e.ESS_name === EssData.selectedEssName
      )
      if (target) {
        setSelectedESS(target)
        const capexObj = target.capex || {}
        const opexObj = target.opex || {}
        const capex = capexObj.total || 0
        const opexInputMode = opexObj.inputMode || 'REGISTRATION'
        const opex = opexObj.total || 0
        setEssFinancials({
          capex: capex,
          opex: opex,
          period: target.analysisPeriodYears || 20,
          opexInputMode,
        })
      }
    }
  }, [EssData.selectedEssName, fullEssList])

  useEffect(() => {
    setESSLoadData([])
    setLoadCharge([])
    setFlag(false)
    setLoadDataList([])
    setPVDataList([])
    findAllESS()
  }, [])

  useEffect(() => {
    if (essListData && essListData.findAllESS) {
      setFullEssList(essListData.findAllESS)
      setEssNames(essListData.findAllESS.map((ess: any) => ess.ESS_name))
    }
  }, [essListData])

  useEffect(() => {
    const holidayYear = unitDefault?.findAllUnitDefault?.[0]?.holidayYear
    if (!holidayYear) return

    setEssData((prev: any) => ({
      ...prev,
      holidayYear,
    }))
  }, [unitDefault])

  useEffect(() => {
    if (LoadDataList) {
      if (LoadDataList.length !== 0) {
        let tmpFlag = false
        for (let index = 0; index < LoadDataList.length; index++) {
          const element = LoadDataList[index]
          if (element && 'customer' in element && 'plan' in element) {
            if (element.customer !== null && element.plan !== null)
              tmpFlag = true
            else {
              setFlag(false)
              break
            }
          }
        }
        setFlag(tmpFlag)
      } else setFlag(false)
    } else setFlag(false)
  }, [LoadDataList])

  useEffect(() => {
    if (flag && LoadDataList && LoadDataList?.length !== 0) {
      const buildESSLoadData = async () => {
        const ESSLoadDataList = await Promise.all(
          LoadDataList.map(async (element: any) => {
            if (!element?.customer || !element?.plan) return []

            const loadOpt =
              element.customerMeta?.loadOpt ||
              element.plan?.cust?.loadOpt ||
              'realTime'
            const useData = calUsingPV(element.customer, loadOpt)

            if (element.plan.planType === 'peakDeduction') {
              if (!holidaysData || !element.plan.touPeriodResult) return []

              const totalPvGeneration = (
                await Promise.all(
                  (element.plan.buyTOUList || []).map(async (buyTOU: any) => {
                    const pvResult = await findPVData({
                      variables: { val: buyTOU?.cust?._id },
                    })
                    const pvData = pvResult.data
                      ? (Object.values(pvResult.data)[0] as ITimeDataItem[])
                      : []
                    const pvOpt = buyTOU?.cust?.pvOpt || 'realTime'

                    return calUsingPV(pvData, pvOpt).reduce(
                      (total, item) => total + (item.value || 0),
                      0
                    )
                  })
                )
              ).reduce((total, value) => total + value, 0)

              return buildPeakDeductionLoadData({
                loadData: useData,
                totalPvGeneration,
                seasonMapping,
                holidayData: holidaysData,
                touPeriods: element.plan.touPeriodResult,
              }).essLoadData
            }

            return element.plan.essPowerRate
              ? divTimeData(useData, 100 / element.plan.essPowerRate)
              : useData.map((item) => ({ ...item, value: 0 }))
          })
        )

        setESSLoadData(ESSLoadDataList)
      }

      buildESSLoadData()
    }
  }, [flag, LoadDataList, holidaysData])

  useEffect(() => {
    if (
      LoadDataList?.length !== 0 &&
      ESSLoadData.length !== 0 &&
      holidaysData
    ) {
      const result = []
      for (let index = 0; index < LoadDataList.length; index++) {
        const element = LoadDataList[index]
        if (
          element?.customer &&
          element?.plan &&
          ESSLoadData[index]?.length !== 0
        ) {
          const charge = sumMonthNumToTotalNum(
            calMonthlyChargeAsUsage(
              ESSLoadData[index],
              element.plan.touPeriodResult,
              element.plan.chargeUnitResult,
              seasonMapping,
              holidaysData
            )
          )
          const load = ESSLoadData[index].reduce(
            (total, item) => total + item.value,
            0
          )
          let gridCharge = 0
          let gridUnitSum = 0
          switch (element.plan.networkOpt) {
            case 'kepco':
              gridUnitSum =
                element.plan.distributionUnitBaseUnit +
                element.plan.distributionUnitUsageUnit +
                element.plan.lossCostMinUnit +
                element.plan.lossCostMidUnit +
                element.plan.lossCostMaxUnit +
                element.plan.additionalSettlementUnit +
                element.plan.transactionCommissionUnit +
                element.plan.transmissionLossCostUnit +
                element.plan.ectCostUnit -
                element.plan.discountedPriceUnit
              break
            case 'internal':
              gridUnitSum =
                element.plan.internalNetworkUnitBaseUnit +
                element.plan.internalNetworkUnitPowerUnit +
                element.plan.ectCostUnit
              break
            default:
              gridUnitSum = 0
              break
          }
          gridCharge = load * gridUnitSum

          result.push({
            custName: element.plan.cust.name,
            touName: element.plan.name,
            load: Number(load.toFixed(0)),
            charge: Number(charge.toFixed(0)),
            gridCharge: Number(gridCharge.toFixed(0)),
          })
        }
      }
      setLoadCharge(result)
    }
  }, [ESSLoadData, holidaysData])

  const [pvCharge, setPvCharge] = useState<
    { custName: string; touName: string; pv: number; charge: number }[]
  >([])

  useEffect(() => {
    if (PVDataList && PVDataList.length > 0 && holidaysData) {
      const result = []
      for (let index = 0; index < PVDataList.length; index++) {
        const element = PVDataList[index]
        if (element?.customer && element?.plan) {
          const pvOpt =
            element.customerMeta?.pvOpt ||
            element.plan?.cust?.pvOpt ||
            'realTime'
          const usingPV = calUsingPV(element.customer, pvOpt)
          const gen = usingPV.reduce((total, item) => total + item.value, 0)
          const charge = sumMonthNumToTotalNum(
            calMonthlyChargeAsUsage(
              usingPV,
              element.plan.touPeriodResult,
              element.plan.chargeUnitResult,
              seasonMapping,
              holidaysData
            )
          )
          result.push({
            custName: element.plan.cust.name,
            touName: element.plan.name,
            pv: Number(gen.toFixed(0)),
            charge: Number(charge.toFixed(0)),
          })
        }
      }
      setPvCharge(result)
    }
  }, [PVDataList, holidaysData])

  useEffect(() => {
    const fetchData = async () => {
      const dataPromises = pvIdList.map(async (item) => {
        if (!item.plan._id) return null
        const planResult = await findOnePurchasePlanData({
          variables: { val: item.plan._id },
        })
        const plan = planResult.data ? Object.values(planResult.data)[0] : null
        const custId = item.customer._id || (plan as any)?.cust?._id
        if (!custId) {
          return {
            customer: null,
            customerMeta: item.customer,
            plan,
          }
        }
        const customerResult = await findPVData({
          variables: { val: custId },
        })
        return {
          customer: customerResult.data
            ? Object.values(customerResult.data)[0]
            : null,
          customerMeta: item.customer,
          plan,
        }
      })
      Promise.all(dataPromises).then((result) => {
        setPVDataList(result.filter(Boolean))
      })
    }
    fetchData()
  }, [pvIdList])

  useEffect(() => {
    const fetchData = async () => {
      const dataPromises = loadIdList.map(async (item) => {
        if (!item.plan._id) return null
        const planResult = await findOneSellTOUData({
          variables: { val: item.plan._id },
        })
        const plan = planResult.data ? Object.values(planResult.data)[0] : null
        const custId = item.customer._id || (plan as any)?.cust?._id
        if (!custId) {
          return {
            customer: null,
            customerMeta: item.customer,
            plan,
          }
        }
        const customerResult = await findLoadData({
          variables: { val: custId },
        })
        return {
          customer: customerResult.data
            ? Object.values(customerResult.data)[0]
            : null,
          customerMeta: item.customer,
          plan,
        }
      })
      Promise.all(dataPromises).then((result) => {
        setLoadDataList(result.filter(Boolean))
      })
    }
    fetchData()
  }, [loadIdList])

  useEffect(() => {
    if (EssData.holidayYear && EssData.holidayYear !== '') {
      findHolidaysData({
        variables: { val: String(EssData.holidayYear) },
      }).then((result: any) => {
        //@ts-ignore
        if (result.data) setHolidaysData(Object.values(result.data)[0].data)
      })
    }
  }, [EssData.holidayYear])

  useEffect(() => {
    if (pvCharge.length === 0 && loadCharge.length === 0) return

    const baseRevenue = loadCharge.reduce((sum, item) => sum + item.charge, 0)
    const basePvCost = pvCharge.reduce((sum, item) => sum + item.charge, 0)
    const baseGridCost = loadCharge.reduce(
      (sum, item) => sum + item.gridCharge,
      0
    )
    const baseOpex = essFinancials.opex

    const years = essFinancials.period || 20
    const cashFlows = [-essFinancials.capex]
    const chartX = ['초기투자']
    const chartY = [-essFinancials.capex]
    let cumulative = -essFinancials.capex
    const yearlyDetailsArray: YearlyData[] = []

    for (let i = 1; i <= years; i++) {
      const decreaseFactor = Math.pow(1 + smpVariationRate / 100, i - 1)
      const increaseFactor = Math.pow(1 + kepcoIncreaseRate / 100, i - 1)

      const yearlyPvCost = basePvCost * decreaseFactor
      const yearlyRevenue = baseRevenue * increaseFactor
      const yearlyOpex =
        essFinancials.opexInputMode === 'ANALYSIS'
          ? yearlyRevenue * (analysisOpexRateInput / 100)
          : baseOpex
      const yearlyNetCashFlow =
        yearlyRevenue - (yearlyPvCost + baseGridCost + yearlyOpex)

      cashFlows.push(yearlyNetCashFlow)
      cumulative += yearlyNetCashFlow
      chartX.push(`${i}년차`)
      chartY.push(cumulative)
      yearlyDetailsArray.push({
        year: i,
        revenue: yearlyRevenue,
        pvCost: yearlyPvCost,
        gridCost: baseGridCost,
        opex: yearlyOpex,
        cumulative: cumulative,
      })
    }

    const rate = discountRateInput / 100
    const npv = cashFlows.reduce(
      (acc, val, t) => acc + val / Math.pow(1 + rate, t),
      0
    )

    let paybackPeriod = '-'
    for (let i = 0; i < chartY.length; i++) {
      if (chartY[i] > 0) {
        paybackPeriod = i.toString()
        break
      }
    }

    const irr = calculateIRR(cashFlows)

    setResult({
      npv,
      irr,
      paybackPeriod,
      chartDataX: chartX,
      chartDataY: chartY,
      cumulativeCashFlow: cumulative,
      yearlyDetails: yearlyDetailsArray,
    })
  }, [
    pvCharge,
    loadCharge,
    essFinancials,
    analysisOpexRateInput,
    discountRateInput,
    smpVariationRate,
    kepcoIncreaseRate,
  ])

  const getChartOption = (): EChartsOption => {
    if (result.chartDataY.length === 0) {
      return {
        grid: { containLabel: true },
        xAxis: { type: 'category', data: [] },
        yAxis: { type: 'value' },
        series: [],
      }
    }
    return {
      title: {
        text: '누적 현금 흐름',
        left: 'center',
        textStyle: { fontSize: 14, color: '#666' },
      },
      tooltip: {
        trigger: 'axis',
        formatter: (params: any) => {
          const val = params[0].value
          return `${params[0].axisValue}<br/><span style="display:inline-block;margin-right:5px;border-radius:10px;width:10px;height:10px;background-color:${params[0].color};"></span>누적금액: <b>${val.toLocaleString()} 원</b>`
        },
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        top: '15%',
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        data: result.chartDataX,
        axisLine: { lineStyle: { color: '#888' } },
      },
      yAxis: {
        type: 'value',
        name: '(단위: 원)',
        axisLabel: {
          formatter: (value: number) => {
            if (Math.abs(value) >= 100000000)
              return (value / 100000000).toFixed(1) + '억'
            if (Math.abs(value) >= 10000)
              return (value / 10000).toFixed(0) + '만'
            return value.toString()
          },
        },
        splitLine: { lineStyle: { type: 'dashed' } },
      },
      series: [
        {
          name: '누적현금',
          type: 'line',
          smooth: true,
          data: result.chartDataY,
          symbolSize: 6,
          lineStyle: { width: 3 },
          markLine: {
            symbol: ['none', 'none'],
            label: { show: true, formatter: '손익분기점' },
            data: [{ yAxis: 0 }],
            lineStyle: { color: '#333', type: 'solid', width: 1 },
          },
          areaStyle: { opacity: 0.1 },
        },
      ],
    }
  }

  // 총합계 변수 미리 빼두기
  const sumBuyCharge = pvCharge.reduce((acc, cur) => acc + cur.charge, 0)
  const sumGridCharge = loadCharge.reduce((acc, cur) => acc + cur.gridCharge, 0)
  const sumSellCharge = loadCharge.reduce((acc, cur) => acc + cur.charge, 0)
  const sumOpex =
    essFinancials.opexInputMode === 'ANALYSIS'
      ? sumSellCharge * (analysisOpexRateInput / 100)
      : essFinancials.opex

  return (
    <ContentWrapper>
      <Header>
        <Title>ESS 사업성 분석</Title>
      </Header>

      <Body>
        <InputSection>
          <TopControls>
            <ControlItem style={{ width: '250px' }}>
              <InputLabel>ESS 선택</InputLabel>
              <AutoCompleteOnly
                options={essNames}
                placeholder="ESS"
                value={EssData.selectedEssName}
                setData={setEssData}
                fieldName="selectedEssName"
                width={180}
              />
            </ControlItem>

            {essFinancials.opexInputMode === 'ANALYSIS' && (
              <ControlItem style={{ width: '190px' }}>
                <InputLabel>연간 OPEX [판매요금 %]</InputLabel>
                <NumberInput
                  type="number"
                  step="0.1"
                  value={analysisOpexRateInput}
                  onChange={(e) =>
                    setAnalysisOpexRateInput(Number(e.target.value))
                  }
                />
              </ControlItem>
            )}

            <RightAlignedControls>
              <ControlItem style={{ width: '175px' }}>
                <InputLabel>할인율 [%]</InputLabel>
                <NumberInput
                  type="number"
                  step="0.1"
                  value={discountRateInput}
                  onChange={(e) =>
                    setDiscountRateInput(Number(e.target.value))
                  }
                />
              </ControlItem>

              <ControlItem style={{ width: '175px' }}>
                <InputLabel>연간 SMP 변동율 [%]</InputLabel>
                <NumberInput
                  type="number"
                  step="0.1"
                  value={smpVariationRate}
                  onChange={(e) => setSmpVariationRate(Number(e.target.value))}
                  style={{ color: 'blue' }}
                />
              </ControlItem>

              <ControlItem style={{ width: '175px' }}>
                <InputLabel>연간 한전 전기요금 변동률 [%]</InputLabel>
                <NumberInput
                  type="number"
                  step="0.1"
                  value={kepcoIncreaseRate}
                  onChange={(e) =>
                    setKepcoIncreaseRate(Number(e.target.value))
                  }
                  style={{ color: 'red' }}
                />
              </ControlItem>
            </RightAlignedControls>
          </TopControls>

          <EconomicAnalysis
            loadIdList={loadIdList}
            setLoadIdList={setLoadIdList}
            pvIdList={pvIdList}
            setPvIdList={setPvIdList}
          />
        </InputSection>

        <SectionFrame>
          <SectionHeader>
            <SectionMark />
            <SectionTitle>경제성 분석 결과</SectionTitle>
            <SectionDivider />
          </SectionHeader>
          <ResultOverview>
            <ResultColumn>
              <SummaryGrid>
                <SummaryCard
                  title="NPV"
                  subTitle="순현재가치"
                  value={`${Math.round(result.npv / 10000).toLocaleString()} 만원`}
                  color={result.npv > 0 ? '#10B981' : '#EF4444'}
                />
                <SummaryCard
                  title="IRR"
                  subTitle="내부수익률"
                  value={`${result.irr}%`}
                  color="#3B82F6"
                />
                <SummaryCard
                  title="회수기간"
                  subTitle="자본 회수 소요 기간"
                  value={`${result.paybackPeriod}년`}
                />
              </SummaryGrid>

              <ResultPanel>
                <LabelOnly label="1차년도 손익 구조" />
                <ProfitBox>
                  <ProfitBody>
                    <ProfitAmountColumn>
                      <GenerateAmount
                        title="연간 구매 전력량"
                        amount={
                          Number(
                            pvCharge.reduce((total, item) => total + item.pv, 0)
                          ).toLocaleString() ?? 0
                        }
                      />
                      <GenerateAmount
                        title="연간 판매 전력량"
                        amount={
                          Number(
                            loadCharge.reduce(
                              (total, item) => total + item.load,
                              0
                            )
                          ).toLocaleString() ?? 0
                        }
                      />
                    </ProfitAmountColumn>
                    <ProfitDivider />
                    <ProfitDetailColumn>
                      <AggregationProfit
                        title="연간 전력 구매 요금"
                        amount={Number(sumBuyCharge).toLocaleString() ?? 0}
                      />
                      <AggregationProfit
                        title="연간 전력 판매 요금"
                        amount={Number(sumSellCharge).toLocaleString() ?? 0}
                      />
                      <AggregationProfit
                        title="연간 망 이용요금"
                        amount={Number(sumGridCharge).toLocaleString() ?? 0}
                      />
                      <AggregationProfit
                        title="ESS OPEX"
                        amount={Number(sumOpex).toLocaleString() ?? 0}
                      />
                      <ProfitStrongDivider />
                      <ProfitTotalRow>
                        <span>연간 영업 이익</span>
                        <span>
                          {Number(
                            sumSellCharge -
                              sumBuyCharge -
                              sumGridCharge -
                              sumOpex
                          ).toLocaleString()}{' '}
                          원
                        </span>
                      </ProfitTotalRow>
                    </ProfitDetailColumn>
                  </ProfitBody>
                </ProfitBox>
              </ResultPanel>
            </ResultColumn>

            <ResultPanel>
              <LabelOnly label="누적 현금흐름" />
              <GraphBox>
                <ReactECharts
                  option={getChartOption()}
                  style={{ height: '100%', width: '100%' }}
                  notMerge={true}
                />
              </GraphBox>
            </ResultPanel>
          </ResultOverview>
        </SectionFrame>

        <SectionFrame>
          <SectionHeader>
            <SectionMark />
            <SectionTitle>상세 산출 내역</SectionTitle>
            <SectionDivider />
          </SectionHeader>
          <DetailSection>
            <DetailGrid>
              <MiddleItem>
                <LabelOnly label="PV 발전사업자 전력 구매 내역" />
                <MiddleItemContent>
                  <Table>
                    <TableRow>
                      <TableCell style={{ borderTopLeftRadius: '4px' }}>
                        고객
                      </TableCell>
                      <TableCell>고객 요금제</TableCell>
                      <TableCell>발전량</TableCell>
                      <TableCell style={{ borderTopRightRadius: '4px' }}>
                        전력 구매 비용
                      </TableCell>
                    </TableRow>
                    {pvCharge.map((v, index) => (
                      <TableRow key={index}>
                        <TableCell white>{v.custName || ''}</TableCell>
                        <TableCell white>{v.touName || ''}</TableCell>
                        <TableCell white>
                          {v.pv.toLocaleString() || 0} kWh
                        </TableCell>
                        <TableCell white>
                          {v.charge.toLocaleString() || 0} 원
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell
                        flexWeight={2}
                        style={{ borderBottomLeftRadius: '4px' }}
                      >
                        총합계
                      </TableCell>
                      <TableCell white>
                        {Number(
                          pvCharge.reduce((total, item) => total + item.pv, 0)
                        ).toLocaleString() ?? 0}{' '}
                        kWh
                      </TableCell>
                      <TableCell
                        white
                        style={{ borderBottomRightRadius: '4px' }}
                      >
                        {Number(
                          pvCharge.reduce(
                            (total, item) => total + item.charge,
                            0
                          )
                        ).toLocaleString() ?? 0}{' '}
                        원
                      </TableCell>
                    </TableRow>
                  </Table>
                </MiddleItemContent>
              </MiddleItem>

              <MiddleItem>
                <LabelOnly label="수용가 전력 판매 내역" />
                <MiddleItemContent>
                  <Table>
                    <TableRow>
                      <TableCell style={{ borderTopLeftRadius: '4px' }}>
                        고객
                      </TableCell>
                      <TableCell>고객 요금제</TableCell>
                      <TableCell>전력 소비량</TableCell>
                      <TableCell style={{ borderTopRightRadius: '4px' }}>
                        전력 판매 수입
                      </TableCell>
                    </TableRow>
                    {loadCharge.map((v, index) => (
                      <TableRow key={index}>
                        <TableCell white>{v.custName}</TableCell>
                        <TableCell white>{v.touName}</TableCell>
                        <TableCell white>
                          {v.load.toLocaleString()} kWh
                        </TableCell>
                        <TableCell white>
                          {v.charge.toLocaleString()} 원
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell
                        flexWeight={2}
                        style={{ borderBottomLeftRadius: '4px' }}
                      >
                        총합계
                      </TableCell>
                      <TableCell white>
                        {Number(
                          loadCharge.reduce(
                            (total, item) => total + item.load,
                            0
                          )
                        ).toLocaleString() ?? 0}{' '}
                        kWh
                      </TableCell>
                      <TableCell
                        white
                        style={{ borderBottomRightRadius: '4px' }}
                      >
                        {Number(
                          loadCharge.reduce(
                            (total, item) => total + item.charge,
                            0
                          )
                        ).toLocaleString() ?? 0}{' '}
                        원
                      </TableCell>
                    </TableRow>
                  </Table>
                </MiddleItemContent>
              </MiddleItem>

              <MiddleItem>
                <LabelOnly label="망 이용요금" />
                <MiddleItemContent>
                  <Table>
                    <TableRow>
                      <TableCell style={{ borderTopLeftRadius: '4px' }}>
                        고객
                      </TableCell>
                      <TableCell>고객 요금제</TableCell>
                      <TableCell style={{ borderTopRightRadius: '4px' }}>
                        망 이용요금
                      </TableCell>
                    </TableRow>
                    {loadCharge.map((v, index) => (
                      <TableRow key={index}>
                        <TableCell white>{v.custName || ''}</TableCell>
                        <TableCell white>{v.touName || ''}</TableCell>
                        <TableCell white>
                          {v.gridCharge.toLocaleString() || 0} 원
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell
                        flexWeight={2}
                        style={{ borderBottomLeftRadius: '4px' }}
                      >
                        총합계
                      </TableCell>
                      <TableCell
                        white
                        style={{ borderBottomRightRadius: '4px' }}
                      >
                        {Number(
                          loadCharge.reduce(
                            (total, item) => total + item.gridCharge,
                            0
                          )
                        ).toLocaleString() || 0}{' '}
                        원
                      </TableCell>
                    </TableRow>
                  </Table>
                </MiddleItemContent>
              </MiddleItem>

              <MiddleItem>
                <LabelOnly label="ESS 운영 비용" />
                <MiddleItemContent>
                  <Table>
                    <TableRow>
                      <TableCell
                        flexWeight={2}
                        style={{ borderTopLeftRadius: '4px' }}
                      >
                        구분
                      </TableCell>
                      <TableCell style={{ borderTopRightRadius: '4px' }}>
                        금액 (연간)
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell white flexWeight={2}>
                        {essFinancials.opexInputMode === 'ANALYSIS'
                          ? `연간 전력 판매 요금의 ${analysisOpexRateInput}%`
                          : `${selectedESS?.ESS_name || '-'} 운영비`}
                      </TableCell>
                      <TableCell white>{sumOpex.toLocaleString()} 원</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        flexWeight={2}
                        style={{ borderBottomLeftRadius: '4px' }}
                      >
                        총합계
                      </TableCell>
                      <TableCell
                        white
                        style={{ borderBottomRightRadius: '4px' }}
                      >
                        {sumOpex.toLocaleString()} 원
                      </TableCell>
                    </TableRow>
                  </Table>
                </MiddleItemContent>
              </MiddleItem>

              <DetailFullWidth>
                <MiddleItem>
                  <LabelOnly label="연도별 현금흐름 상세 표" />
                  <ScrollTableBox>
                    <Table style={{ border: 'none' }}>
                      <TableRow
                        style={{ position: 'sticky', top: 0, zIndex: 1 }}
                      >
                        <TableCell
                          flexWeight={0.6}
                          style={{ borderTopLeftRadius: '8px' }}
                        >
                          연도
                        </TableCell>
                        <TableCell>전력 판매 수입</TableCell>
                        <TableCell>PV 전력 구매 비용</TableCell>
                        <TableCell>망 이용요금</TableCell>
                        <TableCell>OPEX</TableCell>
                        <TableCell style={{ borderTopRightRadius: '8px' }}>
                          누적 현금흐름
                        </TableCell>
                      </TableRow>

                      <TableRow>
                        <TableCell white flexWeight={0.6}>
                          초기 투자
                        </TableCell>
                        <TableCell white>-</TableCell>
                        <TableCell white>-</TableCell>
                        <TableCell white>-</TableCell>
                        <TableCell white>-</TableCell>
                        <TableCell
                          white
                          style={{ color: '#EF4444', fontWeight: 600 }}
                        >
                          {Number(-essFinancials.capex).toLocaleString()} 원
                        </TableCell>
                      </TableRow>

                      {result.yearlyDetails.map((data, idx) => {
                        const isLast = idx === result.yearlyDetails.length - 1
                        return (
                          <TableRow key={data.year}>
                            <TableCell
                              white
                              flexWeight={0.6}
                              style={
                                isLast ? { borderBottomLeftRadius: '8px' } : {}
                              }
                            >
                              {data.year}년차
                            </TableCell>
                            <TableCell white>
                              {Number(data.revenue.toFixed(0)).toLocaleString()}{' '}
                              원
                            </TableCell>
                            <TableCell white>
                              {Number(data.pvCost.toFixed(0)).toLocaleString()}{' '}
                              원
                            </TableCell>
                            <TableCell white>
                              {Number(
                                data.gridCost.toFixed(0)
                              ).toLocaleString()}{' '}
                              원
                            </TableCell>
                            <TableCell white>
                              {Number(data.opex.toFixed(0)).toLocaleString()} 원
                            </TableCell>
                            <TableCell
                              white
                              style={{
                                fontWeight: 600,
                                color: data.cumulative > 0 ? '#10B981' : '#333',
                                ...(isLast
                                  ? { borderBottomRightRadius: '8px' }
                                  : {}),
                              }}
                            >
                              {Number(
                                data.cumulative.toFixed(0)
                              ).toLocaleString()}{' '}
                              원
                            </TableCell>
                          </TableRow>
                        )
                      })}
                    </Table>
                  </ScrollTableBox>
                </MiddleItem>
              </DetailFullWidth>
            </DetailGrid>
          </DetailSection>
        </SectionFrame>
      </Body>
    </ContentWrapper>
  )
}
